import { Text, Flex } from '@chakra-ui/react'
import _, { isEqual } from 'lodash'
import { Virtuoso } from 'react-virtuoso'

import { InlineLoading } from 'components'

import useIsMobile from 'utils/useIsMobile'

import Alert, { GroupedAlerts } from './Alert'
import InfoPopover from './InfoPopover'
import Legend from './Legend'
import { AlertsFiltersT, defaultAlertsFilters } from 'utils/useAlertsFilters'
import { AlertsFilters } from './AlertsFilters'
import { useMemo, useRef, useState } from 'react'
import { useFilteredAlerts } from './useFilteredAlerts'
import { Button, Spinner } from '@chakra-ui/react'
import { CaretLeft, CaretRight } from '@carbon/icons-react'

const AlertsPagination = ({
  page,
  setPage,
  pagesLength,
  isLoading,
}: {
  page: number
  setPage: (newPage: number) => void
  pagesLength: number
  isLoading: boolean
}) => {
  return (
    <Flex
      justifyContent='center'
      alignItems='center'
      py='1rem'
      mt='auto' // Pushes the pagination to the bottom
    >
      <Button
        variant='outline'
        onClick={() => setPage(Math.max(page - 1, 0))}
        isDisabled={page === 0}
        aria-label='Previous Page'
      >
        <CaretLeft />
      </Button>
      <Text mx='1rem' fontWeight='bold' fontSize='sm'>
        {`${page + 1} / ${pagesLength}`}
      </Text>
      <Button
        variant='outline'
        onClick={() => {
          setPage(Math.min(page + 1, pagesLength - 1))
        }}
        isDisabled={page === pagesLength - 1 || isLoading}
        aria-label='Next Page'
      >
        {isLoading ? <Spinner /> : <CaretRight />}
      </Button>
    </Flex>
  )
}

const PaginatedAlerts = ({
  alerts,
  canFetchMore,
  fetchMore,
  isLoading,
  totalAlerts,
  alertsPerDay,
}: {
  alerts: any[]
  canFetchMore: boolean | undefined
  fetchMore: () => void
  isLoading: boolean
  totalAlerts?: number
  alertsPerDay?: Record<string, number>
}) => {
  const [page, setPage] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const groupedAlerts = _.chunk(alerts, 10)
  const handlePageChange = (newPage: number) => {
    if (newPage === groupedAlerts.length - 1 && canFetchMore) {
      fetchMore()
    }
    setPage(newPage)
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }

  return (
    <Flex direction='column'>
      <Flex
        ref={containerRef}
        flex='1'
        overflowY='auto'
        maxH={'500px'}
        minH={'500px'}
        flexDir={'column'}
      >
        <GroupedAlerts
          alerts={groupedAlerts[page]}
          alertsPerDay={alertsPerDay}
        />
      </Flex>
      <AlertsPagination
        page={page}
        setPage={handlePageChange}
        pagesLength={Math.ceil((totalAlerts ?? 1) / 10)}
        isLoading={isLoading}
      />
    </Flex>
  )
}

const Alerts = ({
  alerts,
  fullPage,
  canFetchMore,
  fetchMore,
  isError,
  isFetched,
  isLoading,
  isFetchingNextPage,
  totalAlerts,
}: {
  alerts: any
  fullPage: boolean | undefined
  isFetched: boolean
  canFetchMore: boolean | undefined
  fetchMore: () => void
  isError: boolean
  isLoading: boolean
  isFetchingNextPage: boolean
  totalAlerts?: number
}) => {
  const groupedAlerts = _.groupBy(alerts, (alert) => alert.datePublished)

  const sortedDates = Object.keys(groupedAlerts).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  )

  const displayDates = sortedDates
  const total = Object.keys(displayDates).length

  const alertsPerDay = Object.keys(groupedAlerts).reduce(
    (acc, day) => ({ ...acc, [day]: groupedAlerts[day].length }),
    {}
  )

  if (isError) {
    return (
      <Flex w='100%' alignItems='center' justifyContent='center'>
        Sorry, something went wrong loading the alerts
      </Flex>
    )
  }
  if (!alerts?.length && isFetched) {
    return (
      <Flex w='100%' alignItems='center' justifyContent='center'>
        No alerts at the moment
      </Flex>
    )
  }
  if (!alerts?.length || isLoading) {
    return (
      <Flex w='100%' alignItems='center' justifyContent='center'>
        <InlineLoading />
      </Flex>
    )
  }

  if (fullPage)
    return (
      <Virtuoso
        style={{ height: '78vh' }}
        totalCount={total}
        endReached={() => {
          canFetchMore && fetchMore()
        }}
        itemContent={(index: number) => {
          return (
            <Alert
              index={index}
              displayDates={displayDates}
              groupedAlerts={groupedAlerts}
              fullPage={fullPage}
            />
          )
        }}
      />
    )

  return (
    <PaginatedAlerts
      alerts={alerts}
      canFetchMore={canFetchMore}
      fetchMore={fetchMore}
      isLoading={isFetchingNextPage}
      totalAlerts={totalAlerts}
      alertsPerDay={alertsPerDay}
    />
  )
}

const AlertsSection = ({ fullPage }: { fullPage?: boolean }) => {
  const [isMobile] = useIsMobile()

  const [filters, setFilters] = useState<AlertsFiltersT>(defaultAlertsFilters)
  function filtersCB(_filters: any) {
    setFilters(_filters)
  }

  const disableTracking = useMemo(
    () => isEqual(filters, defaultAlertsFilters),
    [filters]
  )

  const {
    isLoading,
    data: alerts,
    canFetchMore,
    isFetchingNextPage,
    fetchMore,
    isFetched,
    isError,
    total,
  } = useFilteredAlerts({ maxItems: 100, filters, disableTracking })

  return (
    <Flex
      h={fullPage ? 'calc(100vh - 130px)' : '100%'}
      w='full'
      flexDir='column'
    >
      <Flex
        alignItems='center'
        mb='0.75rem'
        flexDir={isMobile ? 'column' : 'row'}
      >
        <Flex alignItems='center'>
          <Text fontSize={['lg', 'lg', 'xl']} mr='0.5rem' fontWeight={'500'}>
            Alerts
          </Text>
          <InfoPopover />
        </Flex>
        {fullPage && <Legend />}
      </Flex>
      <Flex mb={6}>
        <AlertsFilters filtersCB={filtersCB} />
      </Flex>
      <Alerts
        isFetched={isFetched}
        fullPage={fullPage}
        canFetchMore={canFetchMore}
        fetchMore={fetchMore}
        alerts={alerts}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        isError={isError}
        totalAlerts={total}
      />
    </Flex>
  )
}

export default AlertsSection
